import React from "react";
import {
  Document,
  Font,
  Image,
  Page,
  Path,
  Polygon,
  StyleSheet,
  Svg,
  Text,
  View,
} from "@react-pdf/renderer";
import HNLNavLogo from "@/assets/certificates/hnl-logo.png";
import { CLIMB_PLAN_PDF_ICONS as icons } from "./climbPlanPDFIcons";
import type {
  ClimbPlanPDFData,
  PDFHatSkill,
} from "@/pages/planner/climb-plan/review/pdf/climbPlanPDFData";
import { reviewPrompt } from "@/pages/planner/climb-plan/review/reviewPrompt";
import { tidyReflection } from "@/pages/planner/climb-plan/review/tidyReflection";
import { STUCK_ON_SPOTS } from "@/pages/planner/climb-plan/feelings/climbPlanStickers";
import { climbPlanPDFTexts as texts } from "./climbPlanPDFTexts";
import {
  PDF_ACCENT_BAR_HEIGHT,
  PDF_COLOR,
  PDF_FONT,
  PDF_FOOTER_HEIGHT,
  PDF_HEADER_HEIGHT,
  PDF_TYPE,
} from "./pdfTheme";

// @ts-ignore — .ttf
import ProximaNovaReg from "@/assets/fonts/proximanova_regular.ttf";
import ProximaNovaItalic from "@/assets/fonts/proximanova-regularit-webfont.woff";
// @ts-ignore — .otf
import ProximaNovaBold from "@/assets/fonts/proximanova_bold.otf";
// @ts-ignore — .ttf
import MrDafoe from "@/assets/fonts/MrDafoe-Regular.ttf";

Font.register({ family: PDF_FONT.regular, src: ProximaNovaReg });
Font.register({
  family: PDF_FONT.bold,
  src: ProximaNovaBold,
  fontWeight: "bold",
});
Font.register({ family: PDF_FONT.italic, src: ProximaNovaItalic });
Font.register({ family: PDF_FONT.signature, src: MrDafoe });

/** The sticker spot's height, in points; STUCK_ON_SPOTS offsets scale off it. */
const STICKER_HEIGHT = 56;
const STICKER_SPOTS = STUCK_ON_SPOTS.map(({ rotate, offset }) => ({
  transform: `rotate(${rotate}deg)`,
  top: offset * STICKER_HEIGHT,
}));

/** The sample course sequence's row height, shared by the slab and its point. */
const COURSE_HEIGHT = 20;

/**
 * Page one's After High School list is drawn in a box of FIXED height, sized
 * for six paths - every path a Hat offers - so the page's layout does not
 * depend on how many the climber chose, and What's Inside can be pinned to
 * the page's foot without ever being pushed onto page two. A seventh path
 * would be clipped rather than move the grid.
 */
const PATHS_BOX_HEIGHT = 196;

/**
 * Page one is ONE BLOCK OF FIXED HEIGHT - the page's whole content area, a
 * few points short - with What's Inside pushed to its foot by flex. Nothing
 * on it can move the block, so What's Inside is on page one for every plan.
 * US Letter is 792pt tall; the rest is the page's own padding.
 */
const PAGE_ONE_HEIGHT =
  792 - PDF_ACCENT_BAR_HEIGHT - PDF_HEADER_HEIGHT - PDF_FOOTER_HEIGHT - 12 - 4;

/**
 * Page one is a box of fixed height that cannot break, so the two pieces of
 * CMS prose on it are the only things that could push What's Inside into the
 * footer. Each is cut to the lines it has room for, with an ellipsis; both
 * print in full on their own page later.
 *
 * Three lines of the Hat's blurb is every Hat authored today — the longest is
 * 194 characters, about two and a half lines at this width. The program's
 * intro has no authored limit, and at the card's 8pt four lines is roughly
 * what the preview fixture's two sentences take plus one.
 */
const PAGE_ONE_MAX_LINES = { hatDescription: 3, programIntro: 4 } as const;

const styles = StyleSheet.create({
  page: {
    backgroundColor: PDF_COLOR.white,
    fontFamily: PDF_FONT.regular,
    fontSize: PDF_TYPE.body,
    color: PDF_COLOR.text,
    paddingTop: PDF_ACCENT_BAR_HEIGHT + PDF_HEADER_HEIGHT,
    paddingBottom: PDF_FOOTER_HEIGHT + 12,
    paddingHorizontal: 32,
  },

  accentBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: PDF_ACCENT_BAR_HEIGHT,
    backgroundColor: PDF_COLOR.accent,
  },
  header: {
    position: "absolute",
    top: PDF_ACCENT_BAR_HEIGHT + 12,
    left: 32,
    right: 32,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  logo: { width: 90, height: 18.816 },
  headerRight: { fontSize: PDF_TYPE.small, color: PDF_COLOR.muted },

  footer: {
    position: "absolute",
    bottom: 14,
    left: 32,
    right: 32,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
  },

  // --- Sections ---
  section: { marginTop: 16 },
  sectionTitle: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.sectionTitle,
    color: PDF_COLOR.muted,
    letterSpacing: 0.6,
  },
  sectionSubtitle: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    marginTop: 4,
  },
  /**
   * A MAJOR section: one of the four parts page one's What's Inside grid
   * names. Headed in the climber's name, title case, large and dark, so the
   * four stand above the uppercase captions of the sections nested in them.
   */
  majorSectionTitle: {
    fontFamily: PDF_FONT.bold,
    fontSize: 16,
    color: PDF_COLOR.text,
  },
  majorSectionSubtitle: {
    fontSize: PDF_TYPE.body,
    color: PDF_COLOR.muted,
    marginTop: 4,
  },

  heading: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.heading,
    marginBottom: 2,
  },
  subheading: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.subheading,
    marginTop: 10,
    marginBottom: 4,
  },
  // No italic: the only Proxima Nova faces this repo ships in a format
  // @react-pdf can read are regular and bold, and asking for a style it cannot
  // resolve is a hard throw, not a fallback. Colour carries the distinction.
  caption: { fontSize: PDF_TYPE.small, color: PDF_COLOR.muted },
  /** Page one's line under a band, with room before the cards it introduces. */
  bandIntro: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    marginBottom: 6,
  },
  body: { fontSize: PDF_TYPE.body, lineHeight: 1.45 },
  paragraph: { fontSize: PDF_TYPE.body, lineHeight: 1.45, marginTop: 4 },

  row: { flexDirection: "row", alignItems: "center" },
  /**
   * The text beside an image in a row.
   *
   * `flex: 1` and NOT `flexGrow: 1`. Grow alone leaves the basis at `auto`, so
   * the box measures itself against its own content rather than the space
   * left over — and a long line then runs off the right edge of the page
   * instead of wrapping. `flex` sets grow, shrink and a zero basis together,
   * which is what bounds it to the row.
   */
  rowBody: { flex: 1, minWidth: 0 },
  twoColumn: { flexDirection: "row", flexWrap: "wrap" },
  half: { width: "50%", paddingRight: 10 },
  /** The same column with nothing to sit beside it. */
  full: { width: "100%" },

  facetRow: { flexDirection: "row", alignItems: "center", marginTop: 5 },
  facetIcon: { width: 14, height: 14, marginRight: 5 },
  facetName: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.body },
  facetTier: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    marginLeft: 4,
  },

  // The climber's own result row: a round icon, the name with its interest
  // tier as a chip beside it, and the facet's CMS definition under them.
  scoredRow: { flexDirection: "row", alignItems: "flex-start", marginTop: 14 },
  scoredIcon: {
    width: 26,
    height: 26,
    borderRadius: 13,
    marginRight: 8,
    backgroundColor: PDF_COLOR.white,
  },
  scoredHead: { flexDirection: "row", alignItems: "center", flexWrap: "wrap" },
  scoredName: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.subheading },
  /** A chip beside the name rather than at the start of a card. */
  scoredTierChip: { marginLeft: 6, marginTop: 0 },
  /** An unplaced Work Value: name and definition, no icon, no tier. */
  otherValueRow: { marginTop: 6 },
  otherValueName: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.body },
  /**
   * The Hat's own attributes - its picture and words, its Core types and Work
   * Values, its Perks & Quirks - on one tinted panel, so they read as the
   * HAT's and the climber's fit against them stands out.
   */
  hatPanel: {
    marginTop: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: PDF_COLOR.border,
    borderRadius: 8,
    backgroundColor: PDF_COLOR.surface,
  },
  /** The Top Hat's fit message, called out so it is not lost under the list. */
  fitBox: {
    marginTop: 8,
    padding: 8,
    backgroundColor: PDF_COLOR.purpleSurface,
    borderRadius: 6,
  },
  fitBoxLabel: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.purple,
  },
  fitBoxText: { fontSize: PDF_TYPE.small, lineHeight: 1.45, marginTop: 2 },
  fitTag: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.purple,
    marginLeft: 4,
  },

  // Perks & Quirks: four small cards to a row. Explicit widths and right
  // margins rather than space-between, so a short last row sits left instead
  // of spreading its cards across the page. 4 x 23.5% + 3 x 2% = 100%.
  perkGrid: { flexDirection: "row", flexWrap: "wrap", marginTop: 4 },
  perkCard: {
    width: "23.5%",
    marginRight: "2%",
    marginBottom: 8,
    padding: 7,
    borderWidth: 1,
    borderColor: PDF_COLOR.border,
    borderRadius: 6,
    backgroundColor: PDF_COLOR.white,
    // A column, so the answer can sit on the card's floor: the row stretches
    // every card to its tallest, and the chips then line up across it.
    flexDirection: "column",
  },
  perkCardEndOfRow: { marginRight: 0 },
  perkKind: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.muted,
    letterSpacing: 0.6,
    textTransform: "uppercase",
  },
  perkName: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.body,
    lineHeight: 1.3,
    marginTop: 3,
    // The gap above the answer on the tallest card; the shorter ones get
    // more, from the answer's auto top margin.
    marginBottom: 6,
  },
  perkResponse: {
    alignSelf: "flex-start",
    marginTop: "auto",
    paddingVertical: 2,
    paddingHorizontal: 5,
    borderRadius: 3,
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
  },
  /**
   * Both answers blue, as SmallPerkCard draws them.
   *
   * The card's `Rating` row is `#078dee` whether the climber said "That's for
   * me" or "Yikes" - only the thumb glyph changes. Colouring the two
   * differently here would have the document pass a judgement the Hat does not.
   */
  perkResponseAnswered: {
    backgroundColor: PDF_COLOR.blueSurface,
    color: PDF_COLOR.blue,
  },

  cardTitle: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.subheading },

  reflection: {
    marginTop: 8,
    padding: 8,
    backgroundColor: PDF_COLOR.surface,
    borderLeftWidth: 3,
    borderLeftColor: PDF_COLOR.blue,
    borderRadius: 2,
  },
  reflectionPrompt: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
  },
  reflectionText: { fontSize: PDF_TYPE.body, lineHeight: 1.45, marginTop: 3 },

  /**
   * The Program Spotlight, as the screen draws it: a rounded amber-bordered
   * panel with the spotlight illustration on the left and the overline
   * inside. The screen's gradient is flattened to its warm end; @react-pdf
   * fills a View with one colour.
   */
  spotlight: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderWidth: 2,
    borderColor: PDF_COLOR.accentSoft,
    borderRadius: 12,
    backgroundColor: PDF_COLOR.amberSurface,
  },
  spotlightIcon: { width: 62, height: 62, marginRight: 12 },
  spotlightOverline: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.consideringChipText,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  shoutOuts: {
    marginTop: 8,
    padding: 8,
    backgroundColor: PDF_COLOR.amberSurface,
    borderRadius: 4,
  },
  quote: { fontSize: PDF_TYPE.body, marginTop: 4 },
  quoteAuthor: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    textAlign: "right",
  },

  // --- The identity band and the summary grid (page one) ---
  identity: {
    paddingBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: PDF_COLOR.border,
  },
  identityTop: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
  },
  climberName: { fontFamily: PDF_FONT.bold, fontSize: 24, flex: 1 },
  /**
   * Three spots for the "how are you feeling" stickers, beside the name.
   * Each spot's tilt and height is STUCK_ON_SPOTS. The review arranges its
   * own copies around the Hat's portrait instead — see ReviewFeelingStickers.
   */
  stickers: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 12,
    marginBottom: -4,
    // Lifted above the name's baseline so they sit up by the header, but
    // not so far that they cover the Student ID in it.
    position: "relative",
    top: -10,
  },
  stickerSpot: {
    position: "relative",
    width: 74,
    height: STICKER_HEIGHT,
    marginLeft: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  sticker: { maxWidth: 74, maxHeight: STICKER_HEIGHT, objectFit: "contain" },
  identityMeta: {
    fontSize: PDF_TYPE.body,
    color: PDF_COLOR.muted,
    marginTop: 4,
  },

  /** The Hat, across the full width, as page one's one big statement. */
  hero: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginTop: 13,
    padding: 13,
    paddingBottom: 20,
    borderWidth: 1,
    borderColor: PDF_COLOR.border,
    borderRadius: 8,
    backgroundColor: PDF_COLOR.surface,
  },
  /**
   * A circle. @react-pdf has no `borderRadius: "50%"`, so it is half the box -
   * change the size and this changes with it or the circle becomes a rounded
   * square.
   *
   * 72 AND NOT THE DESIGN'S 86, deliberately: the hero sets page one's height,
   * and everything below it has to fit on that page with it.
   */
  heroImage: { width: 72, height: 72, borderRadius: 36, marginRight: 14 },
  heroName: { fontFamily: PDF_FONT.bold, fontSize: 22, marginTop: 1 },
  heroText: {
    fontSize: PDF_TYPE.subheading,
    lineHeight: 1.4,
    marginTop: 6,
  },

  /**
   * A band heading - the uppercase caption that sits OUTSIDE a card and names
   * a part of the page. Distinct from `cardLabel`, the smaller overline inside
   * one: the band says which part of the plan you are reading, the label says
   * which field you are looking at.
   */
  band: {
    fontFamily: PDF_FONT.bold,
    fontSize: 9.5,
    color: PDF_COLOR.muted,
    letterSpacing: 0.7,
    textTransform: "uppercase",
    marginTop: 12,
    marginBottom: 5,
  },
  /** Two bands side by side, over two columns of cards. */
  bandRow: { flexDirection: "row" },

  /** The page-one grid: two columns with a gutter, each holding its own cards. */
  twoCol: { flexDirection: "row", alignItems: "flex-start" },
  colLeft: { width: "48%", marginRight: "4%" },
  colRight: { width: "48%" },

  /** A white bordered box. Everything on page one lives in one of these. */
  card: {
    padding: 9,
    borderWidth: 1,
    borderColor: PDF_COLOR.border,
    borderRadius: 6,
    backgroundColor: PDF_COLOR.white,
    marginBottom: 6,
  },
  cardRow: { flexDirection: "row", alignItems: "flex-start" },
  cardLabel: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.muted,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  cardValue: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.subheading,
    marginTop: 2,
  },
  cardMeta: { fontSize: PDF_TYPE.small, color: PDF_COLOR.muted, marginTop: 1 },
  /** The After High School paths on page one: a bare list, no border. */
  pathsList: { marginTop: 2, height: PATHS_BOX_HEIGHT, overflow: "hidden" },
  /** The "or" between two After High School paths, ruled either side. */
  orDivider: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 5,
    marginBottom: 3,
  },
  orRule: { flex: 1, height: 1, backgroundColor: PDF_COLOR.border },
  orText: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.muted,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginHorizontal: 6,
  },
  /** The program's own blurb, under its name. */
  cardText: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    lineHeight: 1.45,
    marginTop: 6,
  },
  cardIcon: { width: 30, height: 30, marginRight: 8, objectFit: "contain" },

  /** One facet inside a My Profile card: its icon, then its name. */

  /** The climber's own results, in two labelled rows of small tiles. */
  profileRow: { flexDirection: "row", alignItems: "flex-start", marginTop: 5 },
  /**
   * A fixed-width caption, so the two rows of tiles start in the same place
   * whichever label is longer.
   */
  profileRowLabel: {
    width: 92,
    fontSize: 7,
    color: PDF_COLOR.muted,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    marginTop: 3,
  },
  profileStrip: { flexDirection: "row", flexWrap: "wrap", flex: 1 },
  profileTile: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 14,
    marginTop: 4,
  },
  profileIcon: { width: 16, height: 16, borderRadius: 8, marginRight: 4 },
  profileName: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.small },

  /** What the climber has collected, as a row of counts. */
  tally: {
    flexDirection: "row",
    marginTop: 8,
    padding: 10,
    backgroundColor: PDF_COLOR.surface,
    borderRadius: 6,
  },
  tallyItem: { flex: 1 },
  tallyValue: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.heading },
  tallyLabel: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.muted,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginTop: 1,
  },

  /** A Strength or Goal pill over its skill's name: room for the name to breathe. */
  /** The same gap on the Skills to Build page, where the name has no margin of its own. */
  underChip: { marginTop: 7 },
  /** One of the Hat's skills: its name, and the climber's tag beside it. */
  /**
   * One of the Hat's skills, as a small card: name with the climber's tag
   * beside it, blurb beneath. A picked skill's card is washed in its chip's
   * colour so the three picks read at a glance down a column of nine.
   */
  skillCard: {
    marginTop: 5,
    padding: 7,
    borderWidth: 1,
    borderColor: PDF_COLOR.border,
    borderRadius: 6,
    backgroundColor: PDF_COLOR.white,
  },
  skillCardStrength: {
    backgroundColor: PDF_COLOR.purpleSurface,
    borderColor: PDF_COLOR.strengthChipSurface,
  },
  skillCardGoal: {
    backgroundColor: PDF_COLOR.greenSurface,
    borderColor: PDF_COLOR.goalChip,
  },
  skillHead: { flexDirection: "row", alignItems: "center", flexWrap: "wrap" },
  skillChip: { marginLeft: 6, marginTop: 0 },
  /** A named value under a card's pill, with its type beneath it. */
  summaryItem: { marginTop: 3 },
  chipMeta: { fontSize: 7, color: PDF_COLOR.muted, marginTop: 1 },

  // Two to a row. Explicit widths and right margins rather than
  // space-between, so a lone card on the last row sits left instead of
  // stretching across the page. 2 x 48% + 4% = 100%.
  summaryGrid: { flexDirection: "row", flexWrap: "wrap", marginTop: 18 },
  summaryCard: {
    width: "48%",
    marginRight: "4%",
    marginBottom: 8,
    padding: 8,
    borderWidth: 1,
    borderColor: PDF_COLOR.border,
    borderRadius: 6,
    flexDirection: "row",
    alignItems: "flex-start",
  },
  summaryCardEndOfRow: { marginRight: 0 },
  summaryLabel: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.muted,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  summaryValue: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.subheading,
    marginTop: 2,
  },
  summaryImage: {
    width: 30,
    height: 30,
    marginRight: 8,
    objectFit: "contain",
  },

  // --- Chips, as the Climb Plan's own cards wear them ---
  chipBase: {
    alignSelf: "flex-start",
    marginRight: 4,
    marginTop: 3,
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 6,
    fontFamily: PDF_FONT.bold,
    fontSize: 8,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  chipStrength: {
    backgroundColor: PDF_COLOR.strengthChipSurface,
    color: PDF_COLOR.strengthChipText,
  },
  chipGoal: { backgroundColor: PDF_COLOR.goalChip, color: PDF_COLOR.white },
  chipConsidering: {
    backgroundColor: PDF_COLOR.consideringChipSurface,
    color: PDF_COLOR.consideringChipText,
  },
  // The climber's interest tier in a Core type: green, amber, grey.
  chipHigh: { backgroundColor: PDF_COLOR.goalChip, color: PDF_COLOR.white },
  chipSome: {
    backgroundColor: PDF_COLOR.consideringChipSurface,
    color: PDF_COLOR.consideringChipText,
  },
  chipLow: { backgroundColor: PDF_COLOR.surface, color: PDF_COLOR.muted },
  /** On the chosen school, in its blue row. */
  chipInterested: { backgroundColor: PDF_COLOR.blue, color: PDF_COLOR.white },

  // --- Section titles, now with the module's own illustration ---
  sectionTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: PDF_COLOR.border,
  },
  sectionIcon: { width: 14, height: 14, marginRight: 6 },
  majorSectionIcon: { width: 22, height: 22, marginRight: 8 },

  /**
   * An icon that came from the CMS, at whatever ratio its author drew it.
   *
   * `objectFit: contain` is the point. The default crops to fill the box, so
   * a taller-than-wide icon loses its top and bottom - which is exactly how
   * the Keys to Success icons came out clipped.
   */
  cmsIcon: {
    width: 28,
    height: 28,
    marginRight: 8,
    objectFit: "contain",
  },
  cmsIconSmall: { width: 22, height: 22, objectFit: "contain" },

  pathRow: { flexDirection: "row", alignItems: "flex-start", marginTop: 6 },
  /** A name with its pill beside it - Considering, Goal - over its description. */
  chipTitleRow: { flexDirection: "row", alignItems: "center", marginBottom: 4 },
  pathIconSlot: { width: 28, alignItems: "center" },

  /**
   * A What's Inside entry: centred, and unboxed. A card says "a thing the
   * climber chose"; these name parts of the document, so they sit plainly.
   */
  /**
   * Pinned to the foot of page one, above the footer, whatever the climber
   * chose above it. Absolute, so it takes no room in the flow and the break
   * to page two is decided by the bounded content above.
   */
  /** Padded at the foot so What's Inside sits close to, not on, the footer. */
  pageOne: {
    height: PAGE_ONE_HEIGHT,
    paddingBottom: 40,
    justifyContent: "space-between",
  },
  insideBand: { marginTop: 22 },
  insideItem: { padding: 9, marginBottom: 6, alignItems: "center" },
  insideName: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.subheading,
    textAlign: "center",
  },
  insideText: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    lineHeight: 1.45,
    marginTop: 1,
    textAlign: "center",
  },

  /** What Core Personality and Work Values are, for a first-time reader. */
  infoBox: {
    marginTop: 14,
    padding: 10,
    backgroundColor: PDF_COLOR.surface,
    borderRadius: 6,
  },
  infoItem: { marginTop: 4 },
  infoName: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.small,
  },
  infoText: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    lineHeight: 1.45,
    marginTop: 1,
  },

  /** The muted blurb under a facet's name, from its CMS description. */
  facetDescription: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    marginTop: 1,
  },

  // The same blurb when the CMS authored it as a list. A row per bullet so the
  // mark stays on the first line and the text wraps under itself rather than
  // back under the mark.
  facetBulletRow: { flexDirection: "row", marginTop: 1 },
  facetBulletMark: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    width: 7,
  },
  facetBulletText: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    flex: 1,
  },

  // --- The Schools / Credentials panels, from ProgramDetailBody ---
  panelRow: { flexDirection: "row", marginTop: 8 },
  panel: {
    flex: 1,
    padding: 8,
    borderWidth: 1,
    borderColor: PDF_COLOR.border,
    borderRadius: 8,
  },
  panelGap: { marginRight: 10 },
  panelHeading: { flexDirection: "row", alignItems: "center", marginBottom: 4 },
  panelHeadingIcon: { width: 12, height: 12, marginRight: 5 },
  panelHeadingText: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.body,
  },
  schoolRow: {
    marginTop: 4,
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderRadius: 6,
    backgroundColor: PDF_COLOR.blueSurface,
  },
  schoolName: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.body },
  schoolRowInner: { flexDirection: "row", alignItems: "center" },
  schoolChip: { marginLeft: 6, marginTop: 0 },
  /** Another Hat on this program: its name, then the rating glyph and who gave it. */
  otherHatRow: { flexDirection: "row", alignItems: "center", marginTop: 3 },
  otherHatGlyph: { marginLeft: 6, marginRight: 3 },
  otherHatRating: {
    fontFamily: PDF_FONT.italic,
    fontSize: PDF_TYPE.body,
    color: PDF_COLOR.muted,
  },
  credentialRow: {
    marginTop: 4,
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderRadius: 6,
    backgroundColor: PDF_COLOR.amberRowSurface,
  },

  // --- Sample Course Sequence ---
  // The screen clips each course into a chevron with `clip-path`, which
  // @react-pdf has no equivalent for. The slab and its point are drawn as two
  // adjoining pieces in one fill instead, which reads as the same arrow.
  // A FIXED HEIGHT is what makes that work: the drawn point cannot measure
  // itself against a box whose height depends on its label.
  courseRow: { flexDirection: "row", flexWrap: "wrap" },
  courseItem: { flexDirection: "row", marginTop: 4, marginRight: 5 },
  courseBox: {
    height: COURSE_HEIGHT,
    justifyContent: "center",
    paddingLeft: 8,
    paddingRight: 6,
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: PDF_COLOR.blueSurfaceStrong,
  },
  courseLabel: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.small },

  // --- Connect Your Profile cards ---
  /** Says which path the climber connected through: Core or Building Blocks. */
  connectedLabel: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.muted,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginTop: 10,
  },
  // Two building blocks to a row, and an odd last one stays at half width
  // rather than stretching across the page. 2 x 48% + 4% = 100%.
  blockGrid: { flexDirection: "row", flexWrap: "wrap", alignItems: "stretch" },
  blockCard: { width: "48%", marginRight: "4%" },
  blockCardEndOfRow: { marginRight: 0 },
  blockHead: { flexDirection: "row", alignItems: "flex-start" },
  blockMark: { width: 14, height: 14, marginLeft: 6 },
  blockTypeRow: { flexDirection: "row", alignItems: "center", marginTop: 2 },
  blockTypeIcon: { width: 10, height: 10, marginRight: 4 },

  /** The signed name, as it was written in the sign dialog. */
  signature: {
    fontFamily: PDF_FONT.signature,
    fontSize: 24,
    color: PDF_COLOR.purple,
    marginTop: 4,
  },

  /** The skill a group of shout-outs was given for. */
  quoteFor: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.amberText,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    marginTop: 6,
  },
});

/**
 * Keep a word whole.
 *
 * @react-pdf hyphenates by default, which in a narrow column breaks a name
 * mid-word - "Certified SOLIDWORKS Associate - Additive Manufactur/ing".
 * Registering this globally would reach the career and course PDFs that share
 * the renderer, so it is passed per `<Text>` instead.
 */
const NO_HYPHENS = (word: string) => [word];

/** A section's title rule. `minPresenceAhead` keeps it off a page's last line. */
const SectionTitle = ({
  title,
  subtitle,
  icon,
  major = false,
}: {
  title: string;
  subtitle?: string;
  /** The module's own illustration, from `climbPlanPDFIcons`. */
  icon?: string;
  /** One of the four parts of the plan: large and dark, not a caption. */
  major?: boolean;
}) => (
  <View minPresenceAhead={60}>
    <View style={styles.sectionTitleRow}>
      {!!icon && (
        <Image
          style={major ? styles.majorSectionIcon : styles.sectionIcon}
          src={icon}
        />
      )}
      <Text style={major ? styles.majorSectionTitle : styles.sectionTitle}>
        {title}
      </Text>
    </View>
    {!!subtitle && (
      <Text
        style={major ? styles.majorSectionSubtitle : styles.sectionSubtitle}
      >
        {subtitle}
      </Text>
    )}
  </View>
);

type ChipTone =
  | "strength"
  | "goal"
  | "considering"
  | "high"
  | "some"
  | "low"
  | "interested";

const CHIP_TONE = {
  strength: styles.chipStrength,
  goal: styles.chipGoal,
  considering: styles.chipConsidering,
  high: styles.chipHigh,
  some: styles.chipSome,
  low: styles.chipLow,
  interested: styles.chipInterested,
} as const;

/**
 * A Climb Plan pill, in the colour its own card wears: purple for a Strength,
 * green for a Goal, amber for a path the climber is Considering; and for a
 * Core type's interest tier, green High, amber Some, grey Low.
 */
const Chip = ({
  label,
  tone,
  style,
}: {
  label: string;
  tone: ChipTone;
  style?: React.ComponentProps<typeof Text>["style"];
}) => (
  <Text style={[styles.chipBase, CHIP_TONE[tone], style ?? {}]}>{label}</Text>
);

/** One course in the sample sequence: a tinted slab ending in a point. */
const CourseChevron = ({ name }: { name: string }) => (
  <View style={styles.courseItem} wrap={false}>
    <View style={styles.courseBox}>
      <Text style={styles.courseLabel} hyphenationCallback={NO_HYPHENS}>
        {name}
      </Text>
    </View>
    <Svg width={9} height={COURSE_HEIGHT} viewBox={`0 0 9 ${COURSE_HEIGHT}`}>
      <Polygon
        points={`0,0 9,${COURSE_HEIGHT / 2} 0,${COURSE_HEIGHT}`}
        fill={PDF_COLOR.blueSurfaceStrong}
      />
    </Svg>
  </View>
);

/**
 * One type's column of the Hat's skills: a subheading, then a card per skill
 * with the climber's tag beside the ones they picked and the blurb beneath.
 * An empty column is still drawn with its heading, so the two types keep
 * their sides.
 */
const SkillColumn = ({
  label,
  skills,
}: {
  label: string;
  skills: PDFHatSkill[];
}) => (
  <View style={styles.half}>
    <Text style={styles.subheading}>{label}</Text>
    {skills.map((skill) => (
      <View
        key={skill.name}
        style={[
          styles.skillCard,
          skill.tag === "strength"
            ? styles.skillCardStrength
            : skill.tag === "goal"
              ? styles.skillCardGoal
              : {},
        ]}
        wrap={false}
      >
        <View style={styles.skillHead}>
          <Text style={styles.facetName} hyphenationCallback={NO_HYPHENS}>
            {skill.name}
          </Text>
          {skill.tag === "strength" && (
            <Chip
              label={texts.sections.strength}
              tone="strength"
              style={styles.skillChip}
            />
          )}
          {skill.tag === "goal" && (
            <Chip
              label={texts.sections.goal}
              tone="goal"
              style={styles.skillChip}
            />
          )}
        </View>
        {!!skill.description && (
          <Text style={styles.facetDescription}>{skill.description}</Text>
        )}
      </View>
    ))}
  </View>
);

/** "...for an aspiring <Hat>. <First> has identified one PowerSkill as a strength and two skills as goals." */
const skillsSubtitle = (data: ClimbPlanPDFData): string => {
  const skills = data.skillsToBuild;
  const strengthType = skills?.powerSkills.some((s) => s.tag === "strength")
    ? texts.sections.powerSkill
    : skills?.proSkills.some((s) => s.tag === "strength")
      ? texts.sections.proSkill
      : null;
  const goalCount = [
    ...(skills?.powerSkills ?? []),
    ...(skills?.proSkills ?? []),
  ].filter((s) => s.tag === "goal").length;
  return texts.sections.skillsToBuildSubtitle(
    data.climber.firstName,
    data.topHat?.displayName ?? null,
    strengthType,
    goalCount,
  );
};

/**
 * Only drawn when there is an answer, so the editor's "(optional)" marker is
 * dropped from the prompt — the same rule as the web review's panel.
 */
const Reflection = ({ prompt, text }: { prompt: string; text: string }) =>
  !text ? null : (
    <View style={styles.reflection} wrap={false}>
      <Text style={styles.reflectionPrompt}>{reviewPrompt(prompt)}</Text>
      <Text style={styles.reflectionText}>{tidyReflection(text)}</Text>
    </View>
  );

/**
 * One of the climber's own results: the facet's icon, its name with the
 * interest tier as a chip beside it, and the facet's definition under them.
 *
 * NO BAR, AND NO SCORE. The Core tier is the chip; Work Values carry no tier
 * at all - their scores decide WHICH two the profile shows and are then not
 * shown themselves, so a measure here would be the document publishing a
 * ranking the climber has never seen.
 */
const ScoredFacet = ({
  name,
  description,
  bullets,
  iconSrc,
  tier,
  tierLabel,
}: {
  name: string;
  description?: string;
  bullets?: string[];
  iconSrc?: string;
  tier?: "high" | "some" | "low";
  tierLabel?: string;
}) => (
  <View style={styles.scoredRow} wrap={false}>
    {!!iconSrc && <Image style={styles.scoredIcon} src={iconSrc} />}
    <View style={styles.rowBody}>
      <View style={styles.scoredHead}>
        <Text style={styles.scoredName}>{name}</Text>
        {!!tierLabel && (
          <Chip
            label={tierLabel}
            tone={tier ?? "some"}
            style={styles.scoredTierChip}
          />
        )}
      </View>
      {/* The Core facets are authored as a lead-in and a list of fragments that
          complete it, so they print as the list they are — run together into a
          line they read as broken English whichever punctuation joins them.
          Work Values are authored as prose and fall through to the line. The
          two are the same content, so only one is ever drawn. */}
      {bullets && bullets.length > 0 ? (
        bullets.map((bullet) => (
          <View key={bullet} style={styles.facetBulletRow}>
            <Text style={styles.facetBulletMark}>{"\u2022"}</Text>
            <Text style={styles.facetBulletText}>{bullet}</Text>
          </View>
        ))
      ) : !!description ? (
        <Text style={styles.facetDescription}>{description}</Text>
      ) : null}
    </View>
  </View>
);

/** A Work Value the climber did not place: its name and definition only. */
const OtherValue = ({
  name,
  description,
  bullets,
}: {
  name: string;
  description?: string;
  bullets?: string[];
}) => (
  <View style={styles.otherValueRow} wrap={false}>
    <Text style={styles.otherValueName}>{name}</Text>
    {bullets && bullets.length > 0 ? (
      bullets.map((bullet) => (
        <View key={bullet} style={styles.facetBulletRow}>
          <Text style={styles.facetBulletMark}>{"\u2022"}</Text>
          <Text style={styles.facetBulletText}>{bullet}</Text>
        </View>
      ))
    ) : !!description ? (
      <Text style={styles.facetDescription}>{description}</Text>
    ) : null}
  </View>
);

const Facet = ({
  name,
  iconSrc,
  tierLabel,
  isMatch,
}: {
  name: string;
  iconSrc?: string;
  tierLabel?: string;
  isMatch?: boolean;
}) => (
  <View style={styles.facetRow} wrap={false}>
    {!!iconSrc && <Image style={styles.facetIcon} src={iconSrc} />}
    <Text style={styles.facetName}>{name}</Text>
    {!!tierLabel && <Text style={styles.facetTier}>{tierLabel}</Text>}
    {isMatch && <Text style={styles.fitTag}>{texts.labels.fit}</Text>}
  </View>
);

interface SummaryItem {
  name: string;
  /** The small grey line under it, such as a skill's type. */
  meta?: string;
}

/**
 * A band heading - see `styles.band`.
 *
 * `minPresenceAhead` is how a heading refuses to be the last thing on a page.
 * It belongs on the HEADING, not on a wrapper around the whole section: the
 * wrapper is the thing being split, so asking it for room ahead asks the
 * question too late.
 */
const Band = ({
  children,
  minPresenceAhead,
}: {
  children: string;
  minPresenceAhead?: number;
}) => (
  <Text style={styles.band} minPresenceAhead={minPresenceAhead}>
    {children}
  </Text>
);

/** A labelled card: an overline, a bold value, and an optional blurb under it. */
const LabelledCard = ({
  label,
  value,
  text,
  textMaxLines,
  iconSrc,
}: {
  label: string;
  value: string;
  text?: string;
  /** Lines of `text` to print before an ellipsis. See PAGE_ONE_MAX_LINES. */
  textMaxLines?: number;
  iconSrc?: string;
}) => (
  <View style={styles.card} wrap={false}>
    <View style={styles.cardRow}>
      {!!iconSrc && <Image style={styles.cardIcon} src={iconSrc} />}
      <View style={styles.rowBody}>
        <Text style={styles.cardLabel}>{label}</Text>
        <Text style={styles.cardValue} hyphenationCallback={NO_HYPHENS}>
          {value}
        </Text>
      </View>
    </View>
    {!!text && (
      // Both are styles here, not props: @react-pdf/layout reads them off
      // `node.style`, and without the overflow the cut is silent.
      <Text
        style={[
          styles.cardText,
          { maxLines: textMaxLines, textOverflow: "ellipsis" },
        ]}
      >
        {text}
      </Text>
    )}
  </View>
);

/**
 * The After High School paths, one to a line with an "or" rule between them.
 *
 * A comma list read as a to-do list - every path, in order. The climber is
 * considering these as alternatives, and the divider says so. No box: the
 * band and its intro already frame them.
 */
const PathsList = ({ names }: { names: string[] }) => (
  <View style={styles.pathsList} wrap={false}>
    {names.map((name, index) => (
      <View key={index}>
        {index > 0 && (
          <View style={styles.orDivider}>
            <View style={styles.orRule} />
            <Text style={styles.orText}>{texts.summary.or}</Text>
            <View style={styles.orRule} />
          </View>
        )}
        <Text style={styles.cardValue} hyphenationCallback={NO_HYPHENS}>
          {name}
        </Text>
      </View>
    ))}
  </View>
);

/**
 * High School Priorities: the chosen school on the left, the program on the
 * right with its own blurb.
 *
 * The program's description is the SAME `programIntro` its own page prints
 * later. Shown here too because page one is read on its own - handed to a
 * parent, or printed for a meeting - where a program name with nothing under
 * it says only that a choice was made.
 */
const PrioritiesBand = ({ data }: { data: ClimbPlanPDFData }) => {
  const program = data.chosen.program;
  const school = data.chosen.school;
  if (!program && !school) return null;

  return (
    <View wrap={false}>
      <Band>{texts.summary.highSchoolPriorities}</Band>
      <Text style={styles.bandIntro}>
        {texts.summary.highSchoolPrioritiesIntro(data.climber.firstName)}
      </Text>
      <View style={styles.twoCol}>
        <View style={styles.colLeft}>
          {!!school && (
            <LabelledCard label={texts.summary.highSchool} value={school} />
          )}
        </View>
        <View style={styles.colRight}>
          {!!program && (
            <LabelledCard
              label={texts.summary.program}
              value={program}
              text={data.highSchoolPriorities?.programIntro || undefined}
              textMaxLines={PAGE_ONE_MAX_LINES.programIntro}
            />
          )}
        </View>
      </View>
    </View>
  );
};

/**
 * After High School: the paths the climber is considering, in the left
 * column, in a box of fixed height (PATHS_BOX_HEIGHT). The band itself is
 * always drawn, empty list or not, so page one keeps one shape.
 */
const PathsBand = ({ data }: { data: ClimbPlanPDFData }) => {
  const paths = data.afterHighSchool.filter((path) => path.considering);

  return (
    <View wrap={false}>
      {/* With nothing chosen the words go but the box stays, so the page
          keeps its shape. */}
      {paths.length > 0 && (
        <>
          <Band>{texts.summary.afterHighSchoolBand}</Band>
          <Text style={styles.bandIntro}>
            {texts.summary.afterHighSchoolIntro(
              data.climber.firstName,
              data.topHat?.displayName ?? null,
            )}
          </Text>
        </>
      )}
      <View style={styles.twoCol}>
        <View style={styles.colLeft}>
          <PathsList names={paths.map((path) => path.name)} />
        </View>
      </View>
    </View>
  );
};

/**
 * The climber's rating of a Hat, as the glyph the screen draws: the heart for
 * Loved, the thumb for Liked. The path data is the sentiment glyphs' own,
 * from client-common, in their 40x40 space, cropped to the ink.
 */
const RatingGlyph = ({ sentiment }: { sentiment: "LOVE" | "LIKE" }) =>
  sentiment === "LOVE" ? (
    <Svg width={10} height={9} viewBox="10 11.3946 20 17.2149">
      <Path
        d="M10 17.2465C10 22.1095 14.02 24.7005 16.962 27.0205C18 27.8385 19 28.6095 20 28.6095C21 28.6095 22 27.8395 23.038 27.0195C25.981 24.7015 30 22.1095 30 17.2475C30 12.3845 24.5 8.93546 20 13.6115C15.5 8.93446 10 12.3835 10 17.2465Z"
        fill={PDF_COLOR.love}
      />
    </Svg>
  ) : (
    <Svg width={9} height={10} viewBox="10.2499 10 18.7495 20.7644">
      <Path
        d="M28.27 24.2655L28.975 20.1855C29.0163 19.9464 29.005 19.7011 28.9416 19.4668C28.8783 19.2325 28.7646 19.0149 28.6084 18.8292C28.4522 18.6434 28.2573 18.4941 28.0373 18.3915C27.8173 18.289 27.5776 18.2357 27.335 18.2355H22.154C22.033 18.2355 21.9136 18.2092 21.8038 18.1584C21.6941 18.1076 21.5968 18.0336 21.5185 17.9414C21.4403 17.8492 21.3831 17.741 21.3509 17.6245C21.3187 17.5079 21.3122 17.3858 21.332 17.2665L21.995 13.2215C22.1025 12.5647 22.0719 11.8927 21.905 11.2485C21.8335 10.9823 21.696 10.7385 21.505 10.5397C21.3141 10.3409 21.0761 10.1936 20.813 10.1115L20.668 10.0645C20.3401 9.95919 19.9844 9.98352 19.674 10.1325C19.334 10.2965 19.086 10.5955 18.994 10.9505L18.518 12.7845C18.3664 13.3681 18.1462 13.9317 17.862 14.4635C17.447 15.2405 16.805 15.8635 16.137 16.4385L14.698 17.6785C14.4983 17.851 14.3424 18.0684 14.243 18.3128C14.1436 18.5571 14.1035 18.8216 14.126 19.0845L14.938 28.4775C14.9737 28.8929 15.164 29.2798 15.4711 29.5618C15.7783 29.8437 16.18 30.0003 16.597 30.0005H21.245C24.727 30.0005 27.698 27.5745 28.27 24.2655Z"
        fill={PDF_COLOR.like}
      />
      <Path
        d="M10.968 17.4855C11.1612 17.477 11.3503 17.5436 11.4956 17.6712C11.641 17.7989 11.7314 17.9778 11.748 18.1705L12.718 29.4065C12.7344 29.5738 12.7165 29.7427 12.6655 29.9029C12.6145 30.0631 12.5313 30.2112 12.4211 30.3382C12.3109 30.4652 12.176 30.5683 12.0246 30.6414C11.8731 30.7145 11.7084 30.7559 11.5405 30.7632C11.3725 30.7705 11.2048 30.7435 11.0476 30.6838C10.8905 30.6241 10.7471 30.533 10.6263 30.416C10.5056 30.299 10.4099 30.1587 10.3452 30.0035C10.2805 29.8483 10.2481 29.6816 10.25 29.5135V18.2345C10.25 18.0412 10.3248 17.8554 10.4585 17.7158C10.5923 17.5763 10.7748 17.4937 10.968 17.4855Z"
        fill={PDF_COLOR.like}
      />
    </Svg>
  );

/** One entry of the What's Inside grid: a part of the document and what it holds. */
const InsideCard = ({ name, text }: { name: string; text: string }) => (
  <View style={styles.insideItem} wrap={false}>
    <Text style={styles.insideName} hyphenationCallback={NO_HYPHENS}>
      {name}
    </Text>
    <Text style={styles.insideText}>{text}</Text>
  </View>
);

/**
 * What the rest of the document holds, as a two-by-two grid.
 *
 * The static part of page one. Where the bands above it summarise what the
 * climber chose, this names the four parts that follow so a reader holding
 * only the first sheet knows what the plan contains. It prints whatever the
 * climber has: an entry whose section is empty still says what would be there.
 */
const WhatsInsideBand = ({ data }: { data: ClimbPlanPDFData }) => {
  const first = data.climber.firstName;
  const inside = texts.summary.inside;
  const rows = [
    [inside.profile(first), inside.topHatFit(first)],
    [
      inside.highSchool(first, data.chosen.program),
      inside.educationAndTraining(data.topHat?.displayName ?? null),
    ],
  ];

  return (
    <View style={styles.insideBand} wrap={false}>
      <Band>{texts.summary.whatsInside}</Band>
      {rows.map(([left, right]) => (
        <View key={left.name} style={styles.twoCol}>
          <View style={styles.colLeft}>
            <InsideCard {...left} />
          </View>
          <View style={styles.colRight}>
            <InsideCard {...right} />
          </View>
        </View>
      ))}
    </View>
  );
};

/**
 * A climber's Climb Plan, as a printable document.
 *
 * PURE PROPS, NO HOOKS — @react-pdf renders in its own reconciler outside the
 * React tree, so there is no context to read and no query to await. Everything
 * arrives already flattened and already decided by `buildClimbPlanPDFData`:
 * a section this climber has nothing for is `null` here, and this file never
 * re-derives a rule the review screen owns.
 *
 * PAGINATION IS @react-pdf'S, NOT HAND-COUNTED. The two older PDFs in this
 * directory compute their own page breaks by counting rows, which works
 * because their content is fixed. This one has eight optional sections, four
 * free-text reflections and lists of unbounded length, so the content flows
 * through one `<Page>` and the chrome repeats via `fixed`. The page's padding
 * reserves the header and footer heights — without that, page two starts
 * underneath the logo, because a fixed element repeats without displacing
 * anything.
 *
 * A SECTION NEVER SPLITS ACROSS PAGES. Every section's outer `<View>` is
 * `wrap={false}`, so one that does not fit in what is left of a page starts
 * the next page whole. The limit is @react-pdf's, and it is sharp: a no-wrap
 * block TALLER than a page is neither split nor moved — it runs off the bottom
 * and is clipped. Every section here is bounded well under a page by the
 * product itself (three blocks, two goals, 400-character reflections, the
 * CMS's fixed counts), which is what makes keeping them whole safe. A section
 * that ever gains an unbounded list needs that list broken into its own
 * atomic chunks instead.
 */
export const ClimbPlanPDF: React.FC<{ data: ClimbPlanPDFData }> = ({
  data,
}) => (
  <Document title={`${texts.footer.documentTitle} - ${data.climber.name}`}>
    <Page size="LETTER" style={styles.page}>
      <View style={styles.accentBar} fixed />
      <View style={styles.header} fixed>
        <Image style={styles.logo} src={HNLNavLogo} />
        {/* The Student ID only: the name heads page one, and the stickers sit
            beside it there. The date is in the footer. */}
        {!!data.climber.studentId && (
          <Text style={styles.headerRight}>
            {`${texts.header.studentIdLabel} ${data.climber.studentId}`}
          </Text>
        )}
      </View>

      {/* The only legal place to read the page number. */}
      <View style={styles.footer} fixed>
        <Text>
          {[
            texts.footer.documentTitle,
            data.lastUpdated
              ? `${texts.header.updatedLabel} ${data.lastUpdated}`
              : null,
            data.status,
          ]
            .filter(Boolean)
            .join(" | ")}
        </Text>
        <Text
          render={({ pageNumber, totalPages }) =>
            texts.footer.page(pageNumber, totalPages)
          }
        />
      </View>

      {/* --- PAGE ONE: the whole plan at a glance --- */}
      <View style={styles.pageOne} wrap={false}>
        <View>
          <View style={styles.identity} wrap={false}>
            <View style={styles.identityTop}>
              <Text style={styles.climberName}>
                {texts.identity.title(data.climber.name)}
              </Text>
              {data.stickers.length > 0 && (
                <View style={styles.stickers}>
                  {data.stickers.map((sticker, index) => (
                    <View
                      key={sticker.phrase}
                      style={[styles.stickerSpot, STICKER_SPOTS[index] ?? {}]}
                    >
                      <Image style={styles.sticker} src={sticker.imageSrc} />
                    </View>
                  ))}
                </View>
              )}
            </View>
            {/* The Student ID is in the header now, so it is not repeated here. */}
            <Text style={styles.identityMeta}>
              {[
                data.climber.gradeLabel,
                data.climber.districtName,
                data.climber.schoolName,
              ]
                .filter(Boolean)
                .join(" | ")}
            </Text>
          </View>

          {/* The Hat, with room for its picture and its own words. */}
          {!!data.topHat && (
            <View style={styles.hero} wrap={false}>
              {!!data.topHat.imageSrc && (
                <Image style={styles.heroImage} src={data.topHat.imageSrc} />
              )}
              <View style={styles.rowBody}>
                <Text style={styles.cardLabel}>{texts.summary.topHat}</Text>
                <Text style={styles.heroName}>{data.topHat.displayName}</Text>
                {!!data.topHat.shortDescription && (
                  <Text
                    style={[
                      styles.heroText,
                      {
                        maxLines: PAGE_ONE_MAX_LINES.hatDescription,
                        textOverflow: "ellipsis",
                      },
                    ]}
                  >
                    {data.topHat.shortDescription}
                  </Text>
                )}
              </View>
            </View>
          )}

          <PrioritiesBand data={data} />

          <PathsBand data={data} />
        </View>
        <WhatsInsideBand data={data} />
      </View>

      {/* Everything above is the summary; the detail starts on page two. */}
      <View break />

      {/* --- The climber's own Core Personality and Work Values --- */}
      {(data.myCore.length > 0 || data.myWorkValues.length > 0) && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.myProfile(data.climber.firstName)}
            subtitle={texts.sections.myProfileSubtitle}
            major
          />
          <View style={styles.twoColumn}>
            {data.myCore.length > 0 && (
              // A climber who has not done Work Values leaves half the page
              // blank beside their Core; the Core takes the whole width
              // instead of sitting in a column that has nothing to pair with.
              <View
                style={data.myWorkValues.length > 0 ? styles.half : styles.full}
              >
                <Text style={styles.subheading}>{texts.identity.myCore}</Text>
                {data.myCore.map((facet) => (
                  <ScoredFacet key={facet.name} {...facet} />
                ))}
              </View>
            )}
            {data.myWorkValues.length > 0 && (
              <View style={styles.half}>
                <Text style={styles.subheading}>
                  {texts.identity.myWorkValues}
                </Text>
                {data.myWorkValues.map((facet) => (
                  <ScoredFacet key={facet.name} {...facet} />
                ))}
                {data.otherWorkValues.length > 0 && (
                  <View wrap={false}>
                    <Text style={[styles.subheading, { marginTop: 14 }]}>
                      {texts.sections.otherWorkValues}
                    </Text>
                    {data.otherWorkValues.map((facet) => (
                      <OtherValue key={facet.name} {...facet} />
                    ))}
                  </View>
                )}
              </View>
            )}
          </View>

          {/* The two lists above are this climber's scores; this says what is
              being scored, for a parent or counsellor meeting it cold. */}
          <View style={styles.infoBox} wrap={false}>
            {texts.sections.whatTheseAre.map((item) => (
              <View key={item.name} style={styles.infoItem}>
                <Text style={styles.infoName}>{item.name}</Text>
                <Text style={styles.infoText}>{item.text}</Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* --- Top Hat --- */}
      {!!data.topHat && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.topHat(data.climber.firstName)}
            subtitle={texts.sections.topHatSubtitle(data.climber.firstName)}
            icon={icons.topHat}
            major
          />
          {/* The Hat's attributes, on their own panel; the badges below are
              the climber's, so they sit outside it. */}
          <View style={styles.hatPanel} wrap={false}>
            <View style={styles.row} wrap={false}>
              {!!data.topHat.imageSrc && (
                <Image
                  style={{ width: 48, height: 48, marginRight: 10 }}
                  src={data.topHat.imageSrc}
                />
              )}
              <View style={styles.rowBody}>
                <Text style={styles.heading}>{data.topHat.displayName}</Text>
                <Text style={styles.body}>{data.topHat.shortDescription}</Text>
              </View>
            </View>

            <View style={styles.twoColumn}>
              <View style={styles.half}>
                <Text style={styles.subheading}>
                  {texts.sections.corePersonalityTypes}
                </Text>
                <Text style={styles.caption}>
                  {texts.sections.corePersonalitySubtitle}
                </Text>
                {data.topHat.core.map((entry) => (
                  <Facet key={entry.name} {...entry} />
                ))}
              </View>
              {data.topHat.workValues.length > 0 && (
                <View style={styles.half}>
                  <Text style={styles.subheading}>
                    {texts.sections.workValues}
                  </Text>
                  <Text style={styles.caption}>
                    {texts.sections.workValuesSubtitle}
                  </Text>
                  {data.topHat.workValues.map((entry) => (
                    <Facet key={entry.name} {...entry} />
                  ))}
                  {!!data.topHat.fitMessage && (
                    <View style={styles.fitBox} wrap={false}>
                      <Text style={styles.fitBoxLabel}>
                        {texts.labels.fitReport}
                      </Text>
                      <Text style={styles.fitBoxText}>
                        {data.topHat.fitMessage}
                      </Text>
                    </View>
                  )}
                </View>
              )}
            </View>

            {data.topHat.perksAndQuirks.length > 0 && (
              <View>
                <Text style={styles.subheading}>
                  {texts.sections.perksAndQuirks}
                </Text>
                <View style={styles.perkGrid}>
                  {data.topHat.perksAndQuirks.map((item, index) => (
                    <View
                      key={`${item.kind}-${item.name}`}
                      style={[
                        styles.perkCard,
                        index % 4 === 3 ? styles.perkCardEndOfRow : {},
                      ]}
                      wrap={false}
                    >
                      <Text style={styles.perkKind}>{item.kind}</Text>
                      {/* No hyphenation: at a quarter of the page, @react-pdf's
                        default breaks a word in two on most lines. */}
                      <Text
                        style={styles.perkName}
                        hyphenationCallback={NO_HYPHENS}
                      >
                        {item.name}
                      </Text>
                      {!!item.response && (
                        <Text
                          style={[
                            styles.perkResponse,
                            styles.perkResponseAnswered,
                          ]}
                        >
                          {item.response}
                        </Text>
                      )}
                    </View>
                  ))}
                </View>
              </View>
            )}
          </View>

          {data.topHat.badges.length > 0 && (
            <View>
              <Text style={styles.subheading}>
                {texts.sections.personalityBadges(data.climber.firstName)}
              </Text>
              {data.topHat.badges.map((badge) => (
                <View key={badge.name} wrap={false}>
                  <Text style={styles.body}>{badge.name}</Text>
                  {!!badge.tooltip && (
                    <Text style={styles.cardMeta}>{badge.tooltip}</Text>
                  )}
                </View>
              ))}
            </View>
          )}
        </View>
      )}

      {/* --- Connect Your Profile: no heading of its own, it continues the
          Top Hat Fit page under the Hat's panel and badges --- */}
      <View style={styles.section} wrap={false}>
        {/* Which path the climber took. With no blocks connected the
            reflection is about their Core instead - the same rule as
            `showsCoreAndValues`, read off what the data builder kept. */}
        <Text style={styles.connectedLabel}>
          {data.connectProfile.blocks.length > 0
            ? texts.labels.blocksConnected(data.climber.firstName)
            : texts.labels.coreConnected}
        </Text>
        <View style={styles.blockGrid}>
          {data.connectProfile.blocks.map((block, index) => (
            <View
              key={`${block.activityName}-${index}`}
              style={[
                styles.card,
                styles.blockCard,
                index % 2 === 1 ? styles.blockCardEndOfRow : {},
              ]}
              wrap={false}
            >
              <View style={styles.blockHead}>
                <View style={styles.rowBody}>
                  <Text style={styles.cardTitle}>{block.activityName}</Text>
                  <View style={styles.blockTypeRow}>
                    {!!block.categoryIconSrc && (
                      <Image
                        style={styles.blockTypeIcon}
                        src={block.categoryIconSrc}
                      />
                    )}
                    <Text style={styles.cardMeta}>
                      {[block.categoryName, block.duration]
                        .filter(Boolean)
                        .join(" | ")}
                    </Text>
                  </View>
                </View>
                <Image style={styles.blockMark} src={icons.blocks} />
              </View>
              {!!block.description && (
                <Text style={styles.paragraph}>{block.description}</Text>
              )}
              {block.likes.length > 0 && (
                <Text style={styles.cardMeta}>
                  {texts.labels.likes} {block.likes.join(", ")}
                </Text>
              )}
              {block.dislikes.length > 0 && (
                <Text style={styles.cardMeta}>
                  {texts.labels.dislikes} {block.dislikes.join(", ")}
                </Text>
              )}
            </View>
          ))}
        </View>
        {!!data.connectProfile.reflection && (
          <Text style={styles.subheading}>
            {texts.sections.planningNotes(data.climber.firstName)}
          </Text>
        )}
        <Reflection
          prompt={data.connectProfile.reflectionPrompt}
          text={data.connectProfile.reflection}
        />
      </View>

      {/* --- High School Priorities --- */}
      {!!data.highSchoolPriorities && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.highSchoolPriorities(data.climber.firstName)}
            icon={icons.highSchoolPriorities}
            major
          />
          <Text style={[styles.heading, { marginTop: 8 }]}>
            {data.highSchoolPriorities.programName}
          </Text>
          {!!data.highSchoolPriorities.programIntro && (
            <Text style={styles.paragraph}>
              {data.highSchoolPriorities.programIntro}
            </Text>
          )}

          {/* Two panels side by side, as the program detail draws them. The
              screen shows three schools and hides the rest behind a "+N"
              tooltip; a page has no height to run out of, so every school is
              listed. With no credentials the schools panel takes the width,
              which is what the screen does too. */}
          {(data.highSchoolPriorities.schools.length > 0 ||
            data.highSchoolPriorities.certifications.length > 0) && (
            <View style={styles.panelRow}>
              {data.highSchoolPriorities.schools.length > 0 && (
                <View
                  style={[
                    styles.panel,
                    data.highSchoolPriorities.certifications.length > 0
                      ? styles.panelGap
                      : {},
                  ]}
                >
                  <View style={styles.panelHeading}>
                    <Image
                      style={styles.panelHeadingIcon}
                      src={icons.schoolPin}
                    />
                    <Text style={styles.panelHeadingText}>
                      {texts.sections.availableSchools}
                    </Text>
                  </View>
                  {data.highSchoolPriorities.schools.map((school) => (
                    <View
                      key={school.name}
                      style={styles.schoolRow}
                      wrap={false}
                    >
                      <View style={styles.schoolRowInner}>
                        <Text
                          style={styles.schoolName}
                          hyphenationCallback={NO_HYPHENS}
                        >
                          {school.name}
                        </Text>
                        {school.chosen && (
                          <Chip
                            label={texts.sections.interested}
                            tone="interested"
                            style={styles.schoolChip}
                          />
                        )}
                      </View>
                    </View>
                  ))}
                </View>
              )}

              {data.highSchoolPriorities.certifications.length > 0 && (
                <View style={styles.panel}>
                  <View style={styles.panelHeading}>
                    <Image
                      style={styles.panelHeadingIcon}
                      src={icons.credentialMedal}
                    />
                    <Text style={styles.panelHeadingText}>
                      {texts.sections.availableCredentials}
                    </Text>
                  </View>
                  {data.highSchoolPriorities.certifications.map((cert) => (
                    <View
                      key={cert.name}
                      style={styles.credentialRow}
                      wrap={false}
                    >
                      <Text
                        style={styles.schoolName}
                        hyphenationCallback={NO_HYPHENS}
                      >
                        {cert.name}
                      </Text>
                      {!!cert.description && (
                        <Text
                          style={styles.facetDescription}
                          hyphenationCallback={NO_HYPHENS}
                        >
                          {cert.description}
                        </Text>
                      )}
                    </View>
                  ))}
                </View>
              )}
            </View>
          )}

          {data.highSchoolPriorities.sampleCourses.length > 0 && (
            <View>
              <Text style={styles.subheading}>
                {texts.sections.sampleCourses}
              </Text>
              <View style={styles.courseRow}>
                {data.highSchoolPriorities.sampleCourses.map(
                  (course, index) => (
                    <CourseChevron key={`${course}-${index}`} name={course} />
                  ),
                )}
              </View>
            </View>
          )}

          {data.highSchoolPriorities.otherHats.length > 0 && (
            <View>
              <Text style={styles.subheading}>{texts.sections.otherHats}</Text>
              {data.highSchoolPriorities.otherHats.map((hat) => (
                <View key={hat.name} style={styles.otherHatRow} wrap={false}>
                  <Text style={styles.body}>{hat.name}</Text>
                  {!!hat.sentiment && !!hat.sentimentLabel && (
                    <>
                      <View style={styles.otherHatGlyph}>
                        <RatingGlyph sentiment={hat.sentiment} />
                      </View>
                      <Text style={styles.otherHatRating}>
                        {texts.sections.otherHatRating(
                          data.climber.firstName,
                          hat.sentimentLabel,
                        )}
                      </Text>
                    </>
                  )}
                </View>
              ))}
            </View>
          )}
        </View>
      )}

      {/* --- Program Spotlight --- */}
      {!!data.programSpotlight && (
        <View style={styles.section} wrap={false}>
          {/* No section title: the spotlight belongs to the program above
              it, so its heading sits inside its own panel, as on screen. */}
          <View style={styles.spotlight}>
            <Image style={styles.spotlightIcon} src={icons.spotlight} />
            <View style={styles.rowBody}>
              <Text style={styles.spotlightOverline}>
                {texts.sections.programSpotlight}
              </Text>
              {data.programSpotlight.map((paragraph, index) => (
                <Text key={index} style={styles.paragraph}>
                  {paragraph}
                </Text>
              ))}
            </View>
          </View>
        </View>
      )}

      {/* --- Keys to Success --- */}
      {!!data.keysToSuccess && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.keysToSuccess}
            subtitle={texts.sections.keysToSuccessSubtitle(
              data.climber.firstName,
              data.topHat?.displayName ?? null,
            )}
            icon={icons.keysToSuccess}
          />
          <View style={[styles.row, { marginTop: 8 }]} wrap={false}>
            {!!data.keysToSuccess.iconSrc && (
              <Image style={styles.cmsIcon} src={data.keysToSuccess.iconSrc} />
            )}
            <View style={styles.rowBody}>
              <View style={styles.chipTitleRow}>
                <Text style={styles.cardTitle}>{data.keysToSuccess.name}</Text>
                <View style={{ marginLeft: 6 }}>
                  <Chip label={data.keysToSuccess.goalLabel} tone="goal" />
                </View>
              </View>
              {!!data.keysToSuccess.description && (
                <Text style={styles.body}>
                  {data.keysToSuccess.description}
                </Text>
              )}
            </View>
          </View>
          <Reflection
            prompt={data.keysToSuccess.reflectionPrompt}
            text={data.keysToSuccess.reflection}
          />
        </View>
      )}

      {/* --- CTSOs --- */}
      {data.ctsos.length > 0 && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.ctsos}
            subtitle={texts.sections.ctsosSubtitle(data.climber.districtName)}
            icon={icons.ctsos}
          />
          {data.ctsos.map((ctso) => (
            <View key={ctso.name} style={{ marginTop: 6 }}>
              <Text style={styles.facetName}>{ctso.name}</Text>
              {ctso.paragraphs.map((paragraph, index) => (
                <Text key={index} style={styles.paragraph}>
                  {paragraph}
                </Text>
              ))}
            </View>
          ))}
        </View>
      )}

      {/* --- After High School --- */}
      {data.afterHighSchool.length > 0 && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.afterHighSchool}
            subtitle={texts.sections.afterHighSchoolSubtitle(
              data.climber.firstName,
              data.topHat?.displayName ?? null,
            )}
            icon={icons.afterHighSchool}
            major
          />
          {data.afterHighSchool.map((path) => (
            <View key={path.name} style={styles.pathRow} wrap={false}>
              {/* A fixed slot, drawn empty when a path has no icon, so the
                  paths that do and the paths that do not start their text in
                  the same place. */}
              <View style={styles.pathIconSlot}>
                {!!path.iconSrc && (
                  <Image style={styles.cmsIconSmall} src={path.iconSrc} />
                )}
              </View>
              <View style={styles.rowBody}>
                {/* Every path the Hat offers; the chip marks the ones the
                    climber chose, as the review's cards wear it. */}
                <View style={styles.chipTitleRow}>
                  <Text style={styles.facetName}>{path.name}</Text>
                  {path.considering && (
                    <View style={{ marginLeft: 6 }}>
                      <Chip
                        label={texts.labels.considering}
                        tone="considering"
                      />
                    </View>
                  )}
                </View>
                {/* Authored on the `hat-paths` CMS record and looked up live,
                    so a plan written before the text existed prints it too.
                    Conditional because a path whose description is unauthored,
                    or which the hat has since dropped, has none. */}
                {!!path.definition && (
                  <Text style={styles.body}>{path.definition}</Text>
                )}
              </View>
            </View>
          ))}
          <Reflection
            prompt={data.afterHighSchoolReflection.prompt}
            text={data.afterHighSchoolReflection.text}
          />
        </View>
      )}

      {/* --- Skills to Build --- */}
      {!!data.skillsToBuild && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.skillsToBuild}
            subtitle={skillsSubtitle(data)}
            icon={icons.skillsToBuild}
          />
          <View style={styles.twoColumn}>
            <SkillColumn
              label={texts.sections.powerSkills}
              skills={data.skillsToBuild.powerSkills}
            />
            <SkillColumn
              label={texts.sections.proSkills}
              skills={data.skillsToBuild.proSkills}
            />
          </View>
          <Reflection
            prompt={data.skillsToBuild.reflectionPrompt}
            text={data.skillsToBuild.reflection}
          />
        </View>
      )}

      {/* --- Every Shout Out this climber has been given --- */}
      {data.shoutOutGroups.length > 0 && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.shoutOuts}
            subtitle={texts.sections.shoutOutsSubtitle(data.climber.firstName)}
          />
          <View style={styles.shoutOuts}>
            {/* One heading per skill, its quotes beneath — never a caption
                per quote, which let an uncaptioned quote read as the
                previous skill's. */}
            {data.shoutOutGroups.map((group, groupIndex) => (
              <View key={groupIndex} wrap={false}>
                <Text style={styles.quoteFor}>{group.heading}</Text>
                {group.shoutOuts.map((shoutOut, index) => (
                  <View key={index}>
                    <Text style={styles.quote}>"{shoutOut.message}"</Text>
                    {!!shoutOut.author && (
                      <Text style={styles.quoteAuthor}>{shoutOut.author}</Text>
                    )}
                  </View>
                ))}
              </View>
            ))}
          </View>
        </View>
      )}

      {/* The signed name and date, which the review screen never shows. */}
      {!!data.signature && (
        <View style={[styles.section, { marginTop: 20 }]} wrap={false}>
          <Text style={styles.sectionTitle}>{texts.identity.signedBy}</Text>
          <Text style={styles.signature}>{data.signature.name}</Text>
          <Text style={styles.cardMeta}>{data.signature.signedOn}</Text>
        </View>
      )}
    </Page>
  </Document>
);

export default ClimbPlanPDF;
